import { useEffect, useState, type ReactNode } from 'react';
import type { FieldOption, LookupDef } from '@jfh/intake-shared';
import { api } from '../../lib/api.js';
import { useToast } from '../../components/Toast.js';
import { Icon } from '../../components/Icon.js';
import { columnsOf, keyFromName, lookupUsage, toggleExtra, type SampleColumn } from './logic.js';

type Usage = ReturnType<typeof lookupUsage>;

function blankDef(): LookupDef {
  return { key: '', name: '', mode: 'manual', values: [], auth: 'bearer' };
}

/**
 * System set-up → Lookup lists (F9 P5): every list with where its answers
 * come from, how many there are and which questions use it; a guided editor
 * that picks iQ columns from a real record instead of typing field names.
 */
export function LookupLists() {
  const toast = useToast();
  const [lookups, setLookups] = useState<LookupDef[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number | null>>({});
  const [usage, setUsage] = useState<Usage>(new Map());
  const [editing, setEditing] = useState<LookupDef | null>(null);
  // The original key when editing an existing list (null = new list).
  const [originalKey, setOriginalKey] = useState<string | null>(null);

  const load = () => {
    api
      .listLookups()
      .then((r) => {
        setLookups(r.lookups);
        for (const d of r.lookups) {
          if (d.mode === 'manual') continue;
          api
            .getLookup(d.key)
            .then((o) => setCounts((c) => ({ ...c, [d.key]: o.options.length })))
            .catch(() => setCounts((c) => ({ ...c, [d.key]: null })));
        }
      })
      .catch(() => setLookups([]));
  };

  useEffect(() => {
    load();
    // Which questions use each list: every form's draft questions.
    api
      .listForms()
      .then(async ({ forms }) => {
        const details = await Promise.all(forms.map((f) => api.getForm(f.id).then((r) => r.form).catch(() => null)));
        setUsage(
          lookupUsage(details.flatMap((d) => (d ? [{ id: d.id, name: d.name, fields: d.draftSchema }] : []))),
        );
      })
      .catch(() => undefined);
  }, []);

  const close = () => {
    setEditing(null);
    setOriginalKey(null);
  };

  const remove = async (d: LookupDef) => {
    const used = usage.get(d.key)?.length ?? 0;
    const warn = used ? ` ${used} ${used === 1 ? 'question uses' : 'questions use'} it and would have no answers.` : '';
    if (!window.confirm(`Delete the list “${d.name || d.key}”?${warn}`)) return;
    try {
      await api.deleteLookup(d.key);
      toast({ title: 'List deleted', icon: 'check' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', body: (e as Error).message, icon: 'shield' });
    }
  };

  if (editing) {
    return (
      <LookupEditor
        def={editing}
        originalKey={originalKey}
        usedBy={usage.get(originalKey ?? '') ?? []}
        onChange={setEditing}
        onCancel={close}
        onSaved={() => {
          close();
          load();
        }}
      />
    );
  }

  return (
    <div className="card" style={{ padding: 'var(--pad-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <div>
          <strong style={{ fontSize: 14.5 }}>Lookup lists</strong>
          <p className="field-help" style={{ marginTop: 4 }}>
            Lists of answers that questions can use, typed in here or kept up to date from iQ.
          </p>
        </div>
        <button
          className="btn sm"
          onClick={() => {
            setOriginalKey(null);
            setEditing(blankDef());
          }}
        >
          <Icon name="plus" size={14} /> Add a list
        </button>
      </div>

      {lookups === null ? (
        <div style={{ color: 'var(--text-3)', marginTop: 12 }}>Loading…</div>
      ) : lookups.length === 0 ? (
        <div className="field-help" style={{ marginTop: 12 }}>
          No lists yet.
        </div>
      ) : (
        <div style={{ marginTop: 12, border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'hidden' }}>
          <div className="lk-row lk-head">
            <span>List</span>
            <span>Answers come from</span>
            <span>Answers</span>
            <span>Used by</span>
            <span />
          </div>
          {lookups.map((d) => {
            const used = usage.get(d.key) ?? [];
            const count = d.mode === 'manual' ? (d.values ?? []).length : counts[d.key];
            return (
              <div key={d.key} className="lk-row">
                <span style={{ minWidth: 0 }}>
                  <strong style={{ fontSize: 13.5 }}>{d.name || d.key}</strong>
                  <br />
                  <span className="mono" style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                    {d.key}
                  </span>
                </span>
                <span>
                  <span className={`badge ${d.mode === 'api' ? 'new' : 'neutral'}`}>
                    {d.mode === 'api' ? 'iQ' : 'Typed in here'}
                  </span>
                </span>
                <span style={{ fontSize: 13 }}>{count === undefined ? '…' : count === null ? '—' : count}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-2)', minWidth: 0 }} title={used.map((u) => `${u.formName}: ${u.question}`).join('\n')}>
                  {used.length === 0
                    ? 'Not used'
                    : used.length === 1
                      ? `${used[0]!.formName}: ${used[0]!.question}`
                      : `${used.length} questions on ${new Set(used.map((u) => u.formId)).size} ${
                          new Set(used.map((u) => u.formId)).size === 1 ? 'form' : 'forms'
                        }`}
                </span>
                <span style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                  <button
                    className="btn sm"
                    onClick={() => {
                      setOriginalKey(d.key);
                      setEditing({ ...d, values: d.values ? [...d.values] : [] });
                    }}
                  >
                    Edit
                  </button>
                  <button className="icon-btn" aria-label={`Delete ${d.name || d.key}`} onClick={() => void remove(d)}>
                    <Icon name="x" size={14} />
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Step({ n, title, hint, children }: { n: number; title: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '28px 1fr', gap: 10, marginTop: 18 }}>
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: 99,
          background: 'var(--accent-soft)',
          color: 'var(--accent-text)',
          fontWeight: 800,
          fontSize: 12,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {n}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 13.5 }}>{title}</div>
        {hint && <div className="field-help" style={{ marginTop: 2 }}>{hint}</div>}
        <div style={{ marginTop: 8 }}>{children}</div>
      </div>
    </div>
  );
}

function LookupEditor({
  def,
  originalKey,
  usedBy,
  onChange,
  onCancel,
  onSaved,
}: {
  def: LookupDef;
  originalKey: string | null;
  usedBy: { formName: string; question: string }[];
  onChange: (d: LookupDef) => void;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [saving, setSaving] = useState(false);
  const [sampling, setSampling] = useState(false);
  const [sample, setSample] = useState<{ columns: SampleColumn[]; count: number } | { error: string } | null>(null);
  const [preview, setPreview] = useState<{ options: FieldOption[] } | { error: string } | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [technical, setTechnical] = useState(false);
  const isNew = originalKey === null;

  const patch = (p: Partial<LookupDef>) => onChange({ ...def, ...p });
  const values = def.values ?? [];
  const extras = new Set((def.extraFields ?? []).map((e) => e.path));

  const setName = (name: string) =>
    // A new list's key follows its name until someone sets it by hand.
    patch(isNew && (!def.key || def.key === keyFromName(def.name)) ? { name, key: keyFromName(name) } : { name });

  const fetchSample = async () => {
    setSampling(true);
    setSample(null);
    try {
      const r = await api.sampleLookup(def);
      if (r.error) setSample({ error: r.error });
      else {
        setSample({ columns: columnsOf(r.records), count: r.records.length });
        if (!def.resultsPath && r.resultsPath) patch({ resultsPath: r.resultsPath });
      }
    } catch (e) {
      setSample({ error: (e as Error).message });
    } finally {
      setSampling(false);
    }
  };

  const runPreview = async () => {
    setPreviewing(true);
    setPreview(null);
    try {
      setPreview({ options: (await api.previewLookup(def)).options });
    } catch (e) {
      setPreview({ error: (e as Error).message });
    } finally {
      setPreviewing(false);
    }
  };

  const save = async () => {
    if (!def.key.trim()) {
      toast({ title: 'Give the list a name first', icon: 'shield' });
      return;
    }
    setSaving(true);
    try {
      await api.putLookup(def.key, def);
      // If the key changed on an existing list, remove the old record.
      if (originalKey && originalKey !== def.key) await api.deleteLookup(originalKey).catch(() => undefined);
      toast({ title: 'List saved', icon: 'check' });
      onSaved();
    } catch (e) {
      toast({ title: 'Save failed', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSaving(false);
    }
  };

  const seg = (active: boolean) => ({
    flex: 1,
    padding: '8px 6px',
    borderRadius: 8,
    fontSize: 12.5,
    fontWeight: 700,
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border-strong)'}`,
    background: active ? 'var(--accent-soft)' : 'var(--bg-surface)',
    color: active ? 'var(--accent-text)' : 'var(--text-2)',
  });

  return (
    <div className="card" style={{ padding: 'var(--pad-card)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button className="icon-btn" onClick={onCancel} aria-label="Back to lookup lists">
          <Icon name="arrowLeft" size={16} />
        </button>
        <strong style={{ fontSize: 14.5 }}>{isNew ? 'New lookup list' : `Edit “${def.name || def.key}”`}</strong>
      </div>
      {usedBy.length > 0 && (
        <div className="field-help" style={{ marginTop: 6 }}>
          Used by {usedBy.map((u) => `${u.formName}: ${u.question}`).join('; ')}.
        </div>
      )}

      <Step n={1} title="Name it" hint="What staff see when they pick a list for a question.">
        <input className="input" placeholder="Sales reps" value={def.name} onChange={(e) => setName(e.target.value)} aria-label="List name" />
      </Step>

      <Step n={2} title="Where do the answers come from?">
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" style={seg(def.mode === 'api')} onClick={() => patch({ mode: 'api' })} aria-pressed={def.mode === 'api'}>
            From iQ
          </button>
          <button
            type="button"
            style={seg(def.mode === 'manual')}
            onClick={() => patch({ mode: 'manual' })}
            aria-pressed={def.mode === 'manual'}
          >
            Typed in here
          </button>
        </div>
        {def.mode === 'manual' ? (
          <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 32px', gap: 6, fontSize: 11, fontWeight: 700, color: 'var(--text-3)' }}>
              <span>Applicants see</span>
              <span>Stored and sent to iQ as</span>
              <span />
            </div>
            {values.map((c, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 32px', gap: 6 }}>
                <input
                  className="input"
                  value={c.label}
                  aria-label={`Answer ${i + 1}`}
                  onChange={(e) => patch({ values: values.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })}
                />
                <input
                  className="input mono"
                  value={c.value}
                  aria-label={`Answer ${i + 1} stored as`}
                  onChange={(e) => patch({ values: values.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })}
                />
                <button className="icon-btn" aria-label="Remove answer" onClick={() => patch({ values: values.filter((_, j) => j !== i) })}>
                  <Icon name="x" size={14} />
                </button>
              </div>
            ))}
            <div>
              <button className="btn sm" onClick={() => patch({ values: [...values, { value: '', label: '' }] })}>
                <Icon name="plus" size={14} /> Add an answer
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10, marginTop: 10 }}>
            <div>
              <label className="field-label">Which iQ list?</label>
              <input
                className="input mono"
                placeholder="/salesrep"
                value={def.url ?? ''}
                onChange={(e) => patch({ url: e.target.value })}
                aria-label="iQ list address"
              />
              <div className="field-help" style={{ marginTop: 4 }}>
                The part after iQ’s address (Settings → Connections), or a full web address.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                At most
                <input
                  className="input mono"
                  type="number"
                  min={1}
                  style={{ width: 100 }}
                  placeholder="1000"
                  value={def.fetchLimit ?? ''}
                  onChange={(e) => {
                    const n = parseInt(e.target.value, 10);
                    patch({ fetchLimit: Number.isFinite(n) && n > 0 ? n : undefined });
                  }}
                />
                answers
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={(def.auth ?? 'bearer') === 'bearer'}
                  onChange={(e) => patch({ auth: e.target.checked ? 'bearer' : 'none' })}
                />
                Sign in with the iQ token
              </label>
            </div>
          </div>
        )}
      </Step>

      {def.mode === 'api' && (
        <Step
          n={3}
          title="Pick the columns"
          hint="Fetch a few records from iQ, then choose what’s stored (sent to iQ) and what applicants see."
        >
          <button className="btn sm" onClick={() => void fetchSample()} disabled={sampling || !def.url}>
            <Icon name="db" size={13} /> {sampling ? 'Fetching…' : 'Fetch a sample from iQ'}
          </button>
          {!sample && (def.valueField || def.labelField) && (
            <div className="field-help" style={{ marginTop: 6 }}>
              Now: stored <span className="mono">{def.valueField || '(automatic)'}</span>, shown{' '}
              <span className="mono">{def.labelField || '(automatic)'}</span>
              {extras.size ? (
                <>
                  , also kept <span className="mono">{[...extras].join(', ')}</span>
                </>
              ) : null}
              .
            </div>
          )}
          {sample && 'error' in sample && (
            <div className="field-help" style={{ marginTop: 6, color: 'var(--st-declined)' }}>
              Couldn’t fetch a sample: {sample.error}
            </div>
          )}
          {sample && 'columns' in sample && (
            <div style={{ marginTop: 10, border: '1px solid var(--border)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
              <div className="lk-cols lk-head">
                <span>Column</span>
                <span>Example</span>
                <span>Stored</span>
                <span>Shown</span>
                <span>Keep too</span>
              </div>
              {sample.columns.map((c) => (
                <div key={c.path} className="lk-cols">
                  <span className="mono" style={{ overflowWrap: 'anywhere' }}>
                    {c.path}
                  </span>
                  <span style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.sample || '—'}
                  </span>
                  <input
                    type="radio"
                    name="lk-stored"
                    disabled={c.nested}
                    checked={def.valueField === c.path}
                    onChange={() => patch({ valueField: c.path })}
                    aria-label={`Store ${c.path}`}
                  />
                  <input
                    type="radio"
                    name="lk-shown"
                    disabled={c.nested}
                    checked={def.labelField === c.path}
                    onChange={() => patch({ labelField: c.path })}
                    aria-label={`Show ${c.path}`}
                  />
                  <input
                    type="checkbox"
                    checked={extras.has(c.path)}
                    onChange={() => onChange(toggleExtra(def, c.path))}
                    aria-label={`Keep ${c.path} too`}
                  />
                </div>
              ))}
              <div className="field-help" style={{ margin: 0, padding: '8px 12px' }}>
                From {sample.count} {sample.count === 1 ? 'record' : 'records'}. “Keep too” saves a column with each
                answer, for rules (such as “country is in the EU”) and iQ values worked out from the answer.
              </div>
            </div>
          )}
        </Step>
      )}

      <Step n={def.mode === 'api' ? 4 : 3} title="What applicants will see">
        <button className="btn sm" onClick={() => void runPreview()} disabled={previewing}>
          <Icon name="search" size={13} /> {previewing ? 'Loading…' : 'Show the answers'}
        </button>
        {preview && (
          <div style={{ marginTop: 10, padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-sunken)' }}>
            {'error' in preview ? (
              <div style={{ fontSize: 12.5, color: 'var(--st-declined)' }}>Couldn’t load the answers: {preview.error}</div>
            ) : preview.options.length === 0 ? (
              <div className="field-help" style={{ margin: 0 }}>
                No answers. Check the columns you picked.
              </div>
            ) : (
              <>
                <div className="field-label" style={{ marginBottom: 6 }}>
                  {preview.options.length} {preview.options.length === 1 ? 'answer' : 'answers'}
                </div>
                <select className="select" aria-label="Preview of the answers" defaultValue="">
                  <option value="" disabled>
                    Choose…
                  </option>
                  {preview.options.slice(0, 200).map((o, i) => (
                    <option key={i} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <div className="field-help">
                  First few stored as:{' '}
                  {preview.options
                    .slice(0, 3)
                    .map((o) => `${o.label} → ${o.value}`)
                    .join(' · ')}
                </div>
              </>
            )}
          </div>
        )}
      </Step>

      <div style={{ marginTop: 18 }}>
        <button className="btn ghost sm" onClick={() => setTechnical((t) => !t)} aria-expanded={technical}>
          {technical ? 'Hide the technical details' : 'Show the technical details'}
        </button>
        {technical && (
          <div style={{ display: 'grid', gap: 10, marginTop: 8, padding: 12, border: '1px dashed var(--border-strong)', borderRadius: 'var(--r-md)' }}>
            <div>
              <label className="field-label">Key</label>
              <input className="input mono" value={def.key} onChange={(e) => patch({ key: e.target.value })} aria-label="Key" />
              <div className="field-help" style={{ marginTop: 4 }}>
                How questions refer to this list.{!isNew && ' Changing it disconnects the questions using it.'}
              </div>
            </div>
            {def.mode === 'api' && (
              <>
                <div>
                  <label className="field-label">Where the records are in iQ’s answer</label>
                  <input
                    className="input mono"
                    placeholder="(the answer is the list itself)"
                    value={def.resultsPath ?? ''}
                    onChange={(e) => patch({ resultsPath: e.target.value })}
                    aria-label="Results path"
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div>
                    <label className="field-label">Stored column</label>
                    <input className="input mono" placeholder="Code" value={def.valueField ?? ''} onChange={(e) => patch({ valueField: e.target.value })} aria-label="Value field" />
                  </div>
                  <div>
                    <label className="field-label">Shown column</label>
                    <input className="input mono" placeholder="Name" value={def.labelField ?? ''} onChange={(e) => patch({ labelField: e.target.value })} aria-label="Label field" />
                  </div>
                </div>
                <div>
                  <label className="field-label">Kept too</label>
                  {(def.extraFields ?? []).map((ef, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                      <input
                        className="input"
                        style={{ flex: 1 }}
                        placeholder="Name (e.g. postingMap)"
                        value={ef.name}
                        aria-label={`Kept-too name ${i + 1}`}
                        onChange={(e) =>
                          patch({ extraFields: (def.extraFields ?? []).map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })
                        }
                      />
                      <input
                        className="input mono"
                        style={{ flex: 1 }}
                        placeholder="Column (e.g. PostingMap.Code)"
                        value={ef.path}
                        aria-label={`Kept-too column ${i + 1}`}
                        onChange={(e) =>
                          patch({ extraFields: (def.extraFields ?? []).map((x, j) => (j === i ? { ...x, path: e.target.value } : x)) })
                        }
                      />
                      <button
                        className="icon-btn"
                        aria-label="Remove kept-too column"
                        onClick={() => patch({ extraFields: (def.extraFields ?? []).filter((_, j) => j !== i) })}
                      >
                        <Icon name="x" size={14} />
                      </button>
                    </div>
                  ))}
                  <button className="btn sm" onClick={() => patch({ extraFields: [...(def.extraFields ?? []), { name: '', path: '' }] })}>
                    <Icon name="plus" size={13} /> Add a column
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        <button className="btn primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save list'}
        </button>
        <button className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
